/* UserManagement — /admin/users
   Sub-tabs: All Users | New User | Permissions (features list)
   Edit modal: fetches user detail, group permissions by category, checkbox toggle */
import { useState, useMemo } from 'react';
import css from '../AdminPage.module.css';
import {
  useAdminUsers,
  useAdminFeatures,
  useAdminCaseTypes,
  useAdminUserDetail,
  useTogglePermission,
  useUpdateUserRole,
  useUpdateUserCaseTypes,
  useUpdateOwnCasesOnly,
  useCreateAdminUser,
  useSetUserActive,
  useRevokeUserSessions,
} from '../../../hooks/useAdmin';
import { useToast } from '../../../hooks/useToast';
import { usePermissions } from '../../../hooks/usePermissions';
import {
  canManage,
  grantableRoles,
  isSuperadminOrAbove,
  roleLabel,
} from '../../../utils/roleHierarchy';
import { apiErrorMessage, apiErrorStatus } from '../../../utils/apiError';
import {
  useBillingRates,
  useCreateBillingRate,
  useUpdateBillingRate,
} from '../../../hooks/useBillingRates';
import { useActivityCodes } from '../../../hooks/useActivityCodes';
import type { AdminUser } from '../../../types/admin';
import Groups from './Groups';
import OffboardUserModal from './OffboardUserModal';
import Roles from './Roles';

type SubTab = 'users' | 'new-user' | 'permissions' | 'groups' | 'roles';

/** Shown on actions the role ladder does not allow (the server refuses them too). */
const ABOVE_YOUR_LEVEL = "Only someone above this user's level can change them";

/** "22 Oct 2026" */
const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });

export default function UserManagement() {
  const { data: users = [], isLoading, error: usersError } = useAdminUsers();
  const { data: features = [] } = useAdminFeatures();
  const { data: caseTypes = [] } = useAdminCaseTypes();
  const setUserActive = useSetUserActive();
  const revokeSessions = useRevokeUserSessions();
  const { showToast } = useToast();
  const { role: myRole } = usePermissions();

  const [subTab, setSubTab] = useState<SubTab>('users');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  /* edit modal state */
  const [editingUsername, setEditingUsername] = useState<string | null>(null);
  const [offboarding, setOffboarding] = useState<AdminUser | null>(null);
  const { data: userDetail } = useAdminUserDetail(editingUsername);
  /** May the viewer change the user being edited? (role ladder) */
  const canEditDetail = canManage(myRole, userDetail?.role);
  const togglePerm = useTogglePermission();
  const updateRole = useUpdateUserRole();
  const updateCaseTypes = useUpdateUserCaseTypes();
  const updateOwnCasesOnly = useUpdateOwnCasesOnly();
  const { data: userRates = [] } = useBillingRates(editingUsername ?? undefined);
  const createRate = useCreateBillingRate();
  const updateRate = useUpdateBillingRate();
  const { data: activityCodes = [] } = useActivityCodes();

  /* new user form */
  const createUser = useCreateAdminUser();
  const [nuEmail, setNuEmail] = useState('');
  const [nuUsername, setNuUsername] = useState('');
  const [nuFullName, setNuFullName] = useState('');
  const [nuPosition, setNuPosition] = useState('');
  const [nuRole, setNuRole] = useState('user');

  /* billing rates add form */
  const [showAddRate, setShowAddRate] = useState(false);
  const [newRateHourly, setNewRateHourly] = useState('');
  const [newRateFrom, setNewRateFrom] = useState('');
  const [newRateTo, setNewRateTo] = useState('');
  const [newRateActivityId, setNewRateActivityId] = useState('');

  const filtered = useMemo(() => {
    let list = users;
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (u) =>
          u.username.toLowerCase().includes(q) || (u.full_name || '').toLowerCase().includes(q),
      );
    }
    if (roleFilter) list = list.filter((u) => u.role === roleFilter);
    return list;
  }, [users, search, roleFilter]);

  const roles = useMemo(() => [...new Set(users.map((u) => u.role).filter(Boolean))], [users]);

  /* group features by category for the permissions tab & modal */
  const featuresByCategory = useMemo(() => {
    const safe = Array.isArray(features) ? features : [];
    const map = new Map<string, typeof safe>();
    for (const f of safe) {
      const cat = f.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(f);
    }
    return map;
  }, [features]);

  /* group user detail permissions by category */
  type UserFeature = {
    feature_name: string;
    description?: string;
    category?: string;
    has_permission?: boolean;
    permission_source?: string | null;
  };
  const userFeaturesByCategory = useMemo(() => {
    if (!userDetail) return new Map<string, UserFeature[]>();
    const map = new Map<string, UserFeature[]>();
    for (const f of userDetail.all_features) {
      const cat = f.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat)!.push(f);
    }
    return map;
  }, [userDetail]);

  function openEdit(u: AdminUser) {
    setEditingUsername(u.username);
    setShowAddRate(false);
    setNewRateHourly('');
    setNewRateFrom('');
    setNewRateTo('');
    setNewRateActivityId('');
  }

  async function handleRoleChange(newRole: string) {
    if (!editingUsername) return;
    try {
      await updateRole.mutateAsync({ username: editingUsername, role: newRole });
      showToast('Role updated', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to update role'), 'error');
    }
  }

  async function handleTogglePerm(featureName: string, currentlyGranted: boolean) {
    if (!editingUsername) return;
    try {
      await togglePerm.mutateAsync({
        username: editingUsername,
        feature_name: featureName,
        grant: !currentlyGranted,
      });
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to toggle permission'), 'error');
    }
  }

  async function handleAddRate() {
    if (!editingUsername || !newRateHourly || !newRateFrom) return;
    try {
      await createRate.mutateAsync({
        userId: editingUsername,
        hourlyRate: Number(newRateHourly),
        effectiveFrom: newRateFrom,
        effectiveTo: newRateTo || undefined,
        activityCodeId: newRateActivityId ? Number(newRateActivityId) : null,
      });
      showToast('Billing rate added', 'success');
      setNewRateHourly('');
      setNewRateFrom('');
      setNewRateTo('');
      setNewRateActivityId('');
      setShowAddRate(false);
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to add billing rate'), 'error');
    }
  }

  async function handleEndRate(rateId: number) {
    if (!editingUsername) return;
    try {
      await updateRate.mutateAsync({
        id: rateId,
        userId: editingUsername,
        effectiveTo: new Date().toISOString().slice(0, 10),
      });
      showToast('Rate ended', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to end rate'), 'error');
    }
  }

  async function handleCaseTypeToggle(ctId: number) {
    if (!editingUsername || !userDetail) return;
    const current = userDetail.allowed_case_types;
    const next = current.includes(ctId) ? current.filter((x) => x !== ctId) : [...current, ctId];
    try {
      await updateCaseTypes.mutateAsync({ username: editingUsername, case_type_ids: next });
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to update case types'), 'error');
    }
  }

  async function handleSetActive(username: string, active: boolean) {
    const prompt = active
      ? `Reactivate "${username}"? They will be able to log in again.`
      : `Deactivate "${username}"? They will be signed out of every device immediately and unable to log in. Their history is kept and this can be reversed.`;
    if (!confirm(prompt)) return;
    try {
      await setUserActive.mutateAsync({ username, active });
      showToast(active ? 'User reactivated' : 'User deactivated and signed out', 'success');
    } catch (err) {
      showToast(
        apiErrorMessage(err, active ? 'Failed to reactivate user' : 'Failed to deactivate user'),
        'error',
      );
    }
  }

  async function handleRevokeSessions(username: string) {
    if (
      !confirm(
        `End all sessions for "${username}"? They will be signed out of every device and must log in again. Their account stays active.`,
      )
    )
      return;
    try {
      await revokeSessions.mutateAsync(username);
      showToast('All sessions ended', 'success');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to end sessions'), 'error');
    }
  }

  async function handleCreateUser() {
    if (!nuEmail || !nuUsername || !nuFullName) return;
    try {
      await createUser.mutateAsync({
        email: nuEmail,
        username: nuUsername,
        full_name: nuFullName,
        position_in_company: nuPosition || undefined,
        role: nuRole,
      });
      showToast('User created and invitation sent', 'success');
      setNuEmail('');
      setNuUsername('');
      setNuFullName('');
      setNuPosition('');
      setNuRole('user');
      setSubTab('users');
    } catch (err) {
      showToast(apiErrorMessage(err, 'Failed to create user'), 'error');
    }
  }

  if (isLoading) {
    return (
      <div className={css.spinner}>
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
        <div className={css.spinnerDot} />
      </div>
    );
  }

  return (
    <>
      <div className={css.section}>
        <div className={css.sectionHeader}>
          <div>
            <h2 className={css.sectionTitle}>👥 User Management</h2>
            <p className={css.sectionSubtitle}>
              {users.length} user{users.length !== 1 ? 's' : ''} registered
            </p>
          </div>
        </div>

        {/* Sub-tabs */}
        <div className={css.tabs} style={{ marginBottom: 16 }}>
          <button
            className={subTab === 'users' ? css.tabActive : css.tab}
            onClick={() => setSubTab('users')}
          >
            All Users ({users.length})
          </button>
          <button
            className={subTab === 'new-user' ? css.tabActive : css.tab}
            onClick={() => setSubTab('new-user')}
          >
            New User
          </button>
          <button
            className={subTab === 'permissions' ? css.tabActive : css.tab}
            onClick={() => setSubTab('permissions')}
          >
            Permissions
          </button>
          <button
            className={subTab === 'groups' ? css.tabActive : css.tab}
            onClick={() => setSubTab('groups')}
          >
            Groups
          </button>
          <button
            className={subTab === 'roles' ? css.tabActive : css.tab}
            onClick={() => setSubTab('roles')}
          >
            Roles
          </button>
        </div>

        {/* ─── All Users Tab ─── */}
        {subTab === 'users' && (
          <>
            <div className={css.toolbar} style={{ marginBottom: 16 }}>
              <div className={css.searchWrapper}>
                <span className={css.searchIcon}>🔍</span>
                <input
                  className={css.searchInput}
                  placeholder="Search users..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
              <select
                className={css.formSelect}
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                style={{ maxWidth: 160 }}
              >
                <option value="">All Roles</option>
                {roles.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {filtered.length === 0 ? (
              <div className={css.emptyState}>
                <div className={css.emptyIcon}>👤</div>
                <p className={css.emptyMessage}>
                  {!usersError
                    ? 'No users found'
                    : apiErrorStatus(usersError) === 403
                      ? "You don't have permission to view users. Ask an administrator for the manage_users permission."
                      : apiErrorMessage(
                          usersError,
                          'Users could not be loaded. Refresh the page to try again.',
                        )}
                </p>
              </div>
            ) : (
              <table className={css.table}>
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Full Name</th>
                    <th>Role</th>
                    <th>Position</th>
                    <th>Groups</th>
                    <th>Permissions</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((u) => (
                    <tr
                      key={u.username}
                      onClick={() => openEdit(u)}
                      style={{ cursor: 'pointer' }}
                      title="Click to edit"
                    >
                      <td style={{ fontWeight: 600 }}>{u.username}</td>
                      <td>{u.full_name || '—'}</td>
                      <td>
                        <span className={css.badgeInfo}>{roleLabel(u.role)}</span>
                        {u.offboarded_at ? (
                          <span
                            className={css.badgeError}
                            style={{ marginLeft: 4 }}
                            title={`Offboarded on ${new Date(u.offboarded_at).toLocaleString('en-GB')}`}
                          >
                            offboarded {shortDate(u.offboarded_at)}
                          </span>
                        ) : (
                          u.status === 'deactivated' && (
                            <span className={css.badgeWarning} style={{ marginLeft: 4 }}>
                              deactivated
                            </span>
                          )
                        )}
                      </td>
                      <td>{u.position_in_company || '—'}</td>
                      <td>
                        {u.groups && u.groups.length > 0 ? (
                          u.groups.map((g) => (
                            <span
                              key={g.id}
                              className={css.badgeNeutral}
                              style={{ marginRight: 4 }}
                            >
                              {g.name}
                            </span>
                          ))
                        ) : (
                          <span style={{ color: 'var(--text-muted)' }}>—</span>
                        )}
                      </td>
                      <td>
                        <span className={css.badgeNeutral}>{u.permission_count ?? 0}</span>
                      </td>
                      <td>
                        {u.status === 'deactivated' ? (
                          <>
                            <button
                              className={`${css.btnSecondary} ${css.btnSmall}`}
                              style={{ marginRight: 4 }}
                              disabled={u.can_manage === false}
                              title={u.can_manage === false ? ABOVE_YOUR_LEVEL : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetActive(u.username, true);
                              }}
                            >
                              Reactivate
                            </button>
                            <button
                              className={`${css.btnDanger} ${css.btnSmall}`}
                              disabled
                              title={u.offboarded_at ? 'Already offboarded' : 'Already deactivated'}
                              onClick={(e) => e.stopPropagation()}
                            >
                              Deactivate
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              className={`${css.btnSecondary} ${css.btnSmall}`}
                              style={{ marginRight: 4 }}
                              disabled={u.can_manage === false}
                              title={u.can_manage === false ? ABOVE_YOUR_LEVEL : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRevokeSessions(u.username);
                              }}
                            >
                              End sessions
                            </button>
                            <button
                              className={`${css.btnDanger} ${css.btnSmall}`}
                              disabled={u.can_manage === false}
                              title={u.can_manage === false ? ABOVE_YOUR_LEVEL : undefined}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSetActive(u.username, false);
                              }}
                            >
                              Deactivate
                            </button>
                          </>
                        )}
                        <button
                          className={`${css.btnDanger} ${css.btnSmall}`}
                          style={{ marginLeft: 4 }}
                          disabled={u.can_manage === false || !!u.offboarded_at}
                          title={
                            u.can_manage === false
                              ? ABOVE_YOUR_LEVEL
                              : u.offboarded_at
                                ? `Already offboarded on ${shortDate(u.offboarded_at)}`
                                : 'Lock out, remove access and hand over their work'
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            setOffboarding(u);
                          }}
                        >
                          Offboard
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* ─── New User Tab ─── */}
        {subTab === 'new-user' && (
          <div className={css.card} style={{ maxWidth: 600 }}>
            <h3 style={{ margin: '0 0 8px', fontSize: '1.1rem', fontWeight: 600 }}>
              Invite New User
            </h3>
            <p style={{ margin: '0 0 24px', fontSize: '0.875rem', color: '#64748b' }}>
              Create a new staff account. User will receive an email with login credentials.
            </p>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Email Address *</label>
              <input
                className={css.formInput}
                type="email"
                value={nuEmail}
                onChange={(e) => setNuEmail(e.target.value)}
                placeholder="john.smith@yourfirm.com"
              />
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Username *</label>
              <input
                className={css.formInput}
                value={nuUsername}
                onChange={(e) => setNuUsername(e.target.value)}
                placeholder="johnsmith (no spaces, lowercase)"
              />
              <small
                style={{ display: 'block', marginTop: '4px', fontSize: '0.8rem', color: '#64748b' }}
              >
                Used for login (along with email). No spaces or special characters.
              </small>
            </div>

            <div className={css.formGroup}>
              <label className={css.formLabel}>Full Name *</label>
              <input
                className={css.formInput}
                value={nuFullName}
                onChange={(e) => setNuFullName(e.target.value)}
                placeholder="John Smith"
              />
            </div>

            <div className={css.grid2}>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Position</label>
                <input
                  className={css.formInput}
                  value={nuPosition}
                  onChange={(e) => setNuPosition(e.target.value)}
                  placeholder="Solicitor"
                />
              </div>
              <div className={css.formGroup}>
                <label className={css.formLabel}>Role *</label>
                <select
                  className={css.formSelect}
                  value={nuRole}
                  onChange={(e) => setNuRole(e.target.value)}
                >
                  {grantableRoles(myRole).map((r) => (
                    <option key={r} value={r}>
                      {roleLabel(r)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ marginTop: 24, display: 'flex', gap: '12px' }}>
              <button
                className={css.btnPrimary}
                onClick={handleCreateUser}
                disabled={!nuEmail || !nuFullName || createUser.isPending}
              >
                {createUser.isPending ? 'Sending Invitation...' : 'Create & Send Invitation'}
              </button>
              <button
                className={css.btnSecondary}
                onClick={() => {
                  setNuEmail('');
                  setNuUsername('');
                  setNuFullName('');
                  setNuPosition('');
                  setNuRole('user');
                  setSubTab('users');
                }}
                disabled={createUser.isPending}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ─── Permissions Tab — shows all system features grouped by category (read-only) ─── */}
        {subTab === 'groups' && <Groups embedded />}
        {subTab === 'roles' && <Roles embedded />}

        {subTab === 'permissions' && (
          <div>
            <p className={css.sectionSubtitle} style={{ marginBottom: 16 }}>
              All available system features and their descriptions
            </p>
            {featuresByCategory.size === 0 ? (
              <div className={css.emptyState}>
                <p className={css.emptyMessage}>No features configured</p>
              </div>
            ) : (
              [...featuresByCategory.entries()].map(([cat, feats]) => (
                <div key={cat} style={{ marginBottom: 24 }}>
                  <h3
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      fontWeight: 700,
                      color: '#667eea',
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                      marginBottom: 10,
                      borderBottom: '2px solid #667eea',
                      paddingBottom: 4,
                      width: '100%',
                    }}
                  >
                    {cat}{' '}
                    <span style={{ fontWeight: 400, color: 'var(--gray-400)' }}>
                      ({feats.length})
                    </span>
                  </h3>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                      gap: 10,
                    }}
                  >
                    {feats.map((f) => (
                      <div
                        key={f.feature_name}
                        style={{
                          padding: 15,
                          background: 'var(--surface-primary)',
                          border: '1px solid var(--border-primary)',
                          borderRadius: 6,
                        }}
                      >
                        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                          {f.feature_name}
                        </div>
                        {f.description && (
                          <div
                            style={{ fontSize: '0.85em', color: 'var(--gray-500)', marginTop: 4 }}
                          >
                            {f.description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* ── Edit User Modal ── */}
      {editingUsername && (
        <div className={css.modalBackdrop} onClick={() => setEditingUsername(null)}>
          <div
            className={css.modalLg || css.modal}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 700, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className={css.modalHeader}>
              <h3 className={css.modalTitle}>
                Edit User: {editingUsername}
                {userDetail && (
                  <span
                    style={{
                      fontWeight: 400,
                      fontSize: 'var(--font-size-sm)',
                      marginLeft: 8,
                      color: 'var(--gray-500)',
                    }}
                  >
                    {userDetail.all_features.filter((f) => f.has_permission).length} of{' '}
                    {userDetail.all_features.length} permissions
                  </span>
                )}
              </h3>
              <button className={css.modalClose} onClick={() => setEditingUsername(null)}>
                ×
              </button>
            </div>
            <div className={css.modalBody} style={{ overflowY: 'auto', flex: 1 }}>
              {!userDetail ? (
                <div className={css.spinner}>
                  <div className={css.spinnerDot} />
                  <div className={css.spinnerDot} />
                  <div className={css.spinnerDot} />
                </div>
              ) : (
                <>
                  {!canEditDetail && (
                    <p
                      style={{
                        margin: '0 0 16px',
                        padding: '10px 12px',
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--gray-100)',
                        fontSize: 'var(--font-size-sm)',
                      }}
                    >
                      Read only: {userDetail.username} is at or above your level, so only someone
                      higher up can change their access.
                    </p>
                  )}
                  {/* Role */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Role</label>
                    <select
                      className={css.formSelect}
                      value={userDetail.role}
                      onChange={(e) => handleRoleChange(e.target.value)}
                      disabled={updateRole.isPending || !canEditDetail}
                    >
                      {grantableRoles(myRole).map((r) => (
                        <option key={r} value={r}>
                          {roleLabel(r)}
                        </option>
                      ))}
                      {/* Keep the current role visible even when the viewer cannot grant it */}
                      {!grantableRoles(myRole).includes(userDetail.role) && (
                        <option value={userDetail.role} disabled>
                          {roleLabel(userDetail.role)}
                        </option>
                      )}
                    </select>
                  </div>

                  {isSuperadminOrAbove(userDetail.role) && (
                    <p
                      style={{
                        margin: '0 0 12px',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--gray-600)',
                      }}
                    >
                      {roleLabel(userDetail.role)}s have every feature automatically, so the boxes
                      below have no effect. To give limited access, change the role to Admin.
                    </p>
                  )}
                  {/* Permissions grouped by category */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Permissions</label>
                    {[...userFeaturesByCategory.entries()].map(([cat, feats]) => (
                      <div key={cat} style={{ marginBottom: 12 }}>
                        <div
                          style={{
                            fontWeight: 600,
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--gray-500)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.05em',
                            marginBottom: 4,
                          }}
                        >
                          {cat}
                        </div>
                        <div style={{ display: 'grid', gap: 4 }}>
                          {feats.map((f: UserFeature) => (
                            <label
                              key={f.feature_name}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '6px 10px',
                                borderRadius: 'var(--radius-sm)',
                                background: f.has_permission
                                  ? 'var(--primary-50, #eff6ff)'
                                  : 'transparent',
                                border: `1px solid ${f.has_permission ? 'var(--primary-200, #bfdbfe)' : 'var(--gray-200)'}`,
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-sm)',
                                transition: 'all 0.15s',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={!!f.has_permission}
                                onChange={() =>
                                  handleTogglePerm(f.feature_name, !!f.has_permission)
                                }
                                disabled={
                                  !canEditDetail ||
                                  togglePerm.isPending ||
                                  (!!f.has_permission &&
                                    f.permission_source !== 'direct' &&
                                    f.permission_source !== undefined &&
                                    f.permission_source !== null)
                                }
                              />
                              <div>
                                <div style={{ fontWeight: 500 }}>{f.feature_name}</div>
                                {f.description && (
                                  <div
                                    style={{
                                      fontSize: 'var(--font-size-xs)',
                                      color: 'var(--gray-500)',
                                    }}
                                  >
                                    {f.description}
                                  </div>
                                )}
                                {f.has_permission &&
                                  f.permission_source &&
                                  f.permission_source !== 'direct' && (
                                    <div
                                      style={{
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--primary-500)',
                                        marginTop: 2,
                                      }}
                                    >
                                      inherited from: <strong>{f.permission_source}</strong>
                                    </div>
                                  )}
                              </div>
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Case Type Access */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Case Type Access</label>
                    <p
                      style={{
                        fontSize: 'var(--font-size-xs)',
                        color: 'var(--gray-500)',
                        marginBottom: 8,
                      }}
                    >
                      Leave all unchecked for unrestricted access to all case types
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {caseTypes.map((ct) => {
                        const isSelected = userDetail.allowed_case_types.includes(ct.id);
                        return (
                          <button
                            key={ct.id}
                            type="button"
                            onClick={() => handleCaseTypeToggle(ct.id)}
                            disabled={updateCaseTypes.isPending || !canEditDetail}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 6,
                              padding: '5px 12px',
                              borderRadius: 9999,
                              border: `1.5px solid ${isSelected ? 'var(--primary-400)' : 'var(--gray-300)'}`,
                              background: isSelected ? 'var(--primary-50)' : 'var(--gray-50)',
                              color: isSelected ? 'var(--primary-700)' : 'var(--gray-500)',
                              fontWeight: isSelected ? 600 : 400,
                              fontSize: 'var(--font-size-sm)',
                              cursor: updateCaseTypes.isPending ? 'wait' : 'pointer',
                              transition: 'all 0.15s',
                              opacity: updateCaseTypes.isPending ? 0.6 : 1,
                            }}
                          >
                            <span
                              style={{
                                width: 16,
                                height: 16,
                                borderRadius: 4,
                                border: `1.5px solid ${isSelected ? 'var(--primary-500)' : 'var(--gray-300)'}`,
                                background: isSelected ? 'var(--primary-500)' : 'transparent',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: 10,
                                lineHeight: 1,
                              }}
                            >
                              {isSelected ? '\u2713' : ''}
                            </span>
                            {ct.display_name || ct.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Data Scoping */}
                  <div className={css.formGroup}>
                    <label className={css.formLabel}>Data Scoping</label>
                    <label
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 10,
                        cursor: 'pointer',
                        userSelect: 'none',
                        padding: '8px 12px',
                        border: '1px solid var(--gray-200)',
                        borderRadius: 'var(--radius-sm)',
                        background: userDetail.own_cases_only
                          ? 'var(--warning-50, #fffbeb)'
                          : 'var(--gray-50)',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={userDetail.own_cases_only}
                        disabled={updateOwnCasesOnly.isPending || !canEditDetail}
                        onChange={async (e) => {
                          if (!editingUsername) return;
                          // Capture before await — controlled checkbox reverts to old
                          // DB value on re-render, making e.target.checked stale.
                          const newValue = e.target.checked;
                          try {
                            await updateOwnCasesOnly.mutateAsync({
                              username: editingUsername,
                              own_cases_only: newValue,
                            });
                            showToast(
                              newValue
                                ? 'User restricted to assigned cases only'
                                : 'User can access all cases',
                              'success',
                            );
                          } catch (err) {
                            showToast(
                              apiErrorMessage(err, 'Failed to update data scoping'),
                              'error',
                            );
                          }
                        }}
                      />
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                          Restrict to assigned cases only
                        </div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                          When enabled, this user can only view and filter cases assigned to them
                        </div>
                      </div>
                    </label>
                  </div>

                  {/* Billing Rates */}
                  <div className={css.formGroup}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: 8,
                      }}
                    >
                      <label className={css.formLabel} style={{ margin: 0 }}>
                        Billing Rates
                      </label>
                      <button
                        className={`${css.btnPrimary} ${css.btnSmall}`}
                        onClick={() => setShowAddRate((v) => !v)}
                      >
                        {showAddRate ? 'Cancel' : '+ Add Rate'}
                      </button>
                    </div>

                    {showAddRate && (
                      <div
                        style={{
                          padding: '12px 14px',
                          background: 'var(--surface-secondary)',
                          borderRadius: 'var(--radius)',
                          marginBottom: 10,
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '8px 12px',
                        }}
                      >
                        <div className={css.formGroup} style={{ margin: 0 }}>
                          <label className={css.formLabel}>Hourly Rate (£) *</label>
                          <input
                            className={css.formInput}
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="125.00"
                            value={newRateHourly}
                            onChange={(e) => setNewRateHourly(e.target.value)}
                          />
                        </div>
                        <div className={css.formGroup} style={{ margin: 0 }}>
                          <label className={css.formLabel}>Activity Code</label>
                          <select
                            className={css.formSelect}
                            value={newRateActivityId}
                            onChange={(e) => setNewRateActivityId(e.target.value)}
                          >
                            <option value="">All activities</option>
                            {activityCodes.map((ac) => (
                              <option key={ac.id} value={ac.id}>
                                {ac.code} — {ac.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div className={css.formGroup} style={{ margin: 0 }}>
                          <label className={css.formLabel}>Effective From *</label>
                          <input
                            className={css.formInput}
                            type="date"
                            value={newRateFrom}
                            onChange={(e) => setNewRateFrom(e.target.value)}
                          />
                        </div>
                        <div className={css.formGroup} style={{ margin: 0 }}>
                          <label className={css.formLabel}>Effective To</label>
                          <input
                            className={css.formInput}
                            type="date"
                            value={newRateTo}
                            onChange={(e) => setNewRateTo(e.target.value)}
                          />
                        </div>
                        <div
                          style={{
                            gridColumn: '1 / -1',
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <button
                            className={css.btnPrimary}
                            onClick={handleAddRate}
                            disabled={!newRateHourly || !newRateFrom || createRate.isPending}
                          >
                            {createRate.isPending ? 'Saving...' : 'Save Rate'}
                          </button>
                        </div>
                      </div>
                    )}

                    {userRates.length === 0 ? (
                      <p
                        style={{
                          fontSize: 'var(--font-size-xs)',
                          color: 'var(--text-muted)',
                          margin: 0,
                        }}
                      >
                        No billing rates configured — default rates apply.
                      </p>
                    ) : (
                      <table className={css.table} style={{ fontSize: 'var(--font-size-sm)' }}>
                        <thead>
                          <tr>
                            <th>Activity</th>
                            <th>Rate</th>
                            <th>From</th>
                            <th>To</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {userRates.map((r) => {
                            const ac = activityCodes.find((a) => a.id === r.activityCodeId);
                            const isActive =
                              !r.effectiveTo || new Date(r.effectiveTo) >= new Date();
                            return (
                              <tr key={r.id} style={{ opacity: isActive ? 1 : 0.5 }}>
                                <td>
                                  {ac ? (
                                    `${ac.code} — ${ac.name}`
                                  ) : (
                                    <em style={{ color: 'var(--text-muted)' }}>All</em>
                                  )}
                                </td>
                                <td style={{ fontWeight: 600 }}>
                                  £{Number(r.hourlyRate).toFixed(2)}/hr
                                </td>
                                <td>{new Date(r.effectiveFrom).toLocaleDateString('en-GB')}</td>
                                <td>
                                  {r.effectiveTo ? (
                                    new Date(r.effectiveTo).toLocaleDateString('en-GB')
                                  ) : (
                                    <span className={css.badgeSuccess}>Current</span>
                                  )}
                                </td>
                                <td>
                                  {isActive && (
                                    <button
                                      className={`${css.btnDanger} ${css.btnSmall}`}
                                      onClick={() => handleEndRate(r.id)}
                                      disabled={updateRate.isPending}
                                    >
                                      End
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className={css.modalFooter}>
              <button className={css.btnSecondary} onClick={() => setEditingUsername(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {offboarding && (
        <OffboardUserModal
          user={offboarding}
          users={users}
          onClose={() => setOffboarding(null)}
          onOffboarded={(r) => showToast(`User offboarded: ${r.username}`, 'success')}
        />
      )}
    </>
  );
}
